import { areaCodeOf, itemCountry, toCca3 } from '../../../grouping/domain/cartographyGrouping';

import type { CartographyItemInfo } from '../../../grouping/domain/cartographyGrouping';
import type { GroupFamily } from '../../../grouping/domain/grouping.types';

/** Picked area codes by cartography level (`level1`, `level2`...). */
export type PickedLevels = Record<string, string[]>;

/** Screens also carry their geo country name, used when they have no `level0` tag. */
export type LocatableItem = CartographyItemInfo & { geo_country_str?: string | null };

export interface LocationCriteria {
  /** Active picks, one entry per level. */
  active: PickedLevels;
  /** CCA3 the item must belong to; `null` does not filter by country. */
  country: string | null;
  levelIds: string[];
}

/** CCA3 (upper case) an item belongs to: its cartography country, else (screens) its geo country. */
export function locationCountry(family: GroupFamily, item: LocatableItem): string | null {
  return itemCountry(family, item) ?? (family === 'screens' ? toCca3(item.geo_country_str) : null);
}

/** Campaign countries (CCA3, upper case, campaign order) with at least one item. */
export function countriesWithItems(family: GroupFamily, items: LocatableItem[], campaignCountries: string[]): string[] {
  const used = new Set(items.map(item => locationCountry(family, item)));
  const countries = campaignCountries.map(country => toCca3(country));
  return [...new Set(countries.filter((country): country is string => country !== null && used.has(country)))];
}

/**
 * Cascade of the level filters: the areas each level offers come from the items left by the upper levels,
 * and the picked areas that no longer exist there are dropped.
 */
export function resolveLevels(
  items: CartographyItemInfo[],
  levelIds: string[],
  picked: PickedLevels
): { active: PickedLevels; available: PickedLevels } {
  const available: PickedLevels = {};
  const active: PickedLevels = {};
  let scope = items;
  for (const level of levelIds) {
    const codes = new Set(scope.map(item => areaCodeOf(item, level)).filter((code): code is string => code !== null));
    available[level] = [...codes].sort((a, b) => a.localeCompare(b));
    active[level] = (picked[level] ?? []).filter(code => codes.has(code));
    if (active[level].length > 0) {
      scope = scope.filter(item => active[level].includes(areaCodeOf(item, level) ?? ''));
    }
  }
  return { active, available };
}

/** AND between the country and the levels, OR inside a level; an item without area at a filtered level fails. */
export function matchesLocation(
  family: GroupFamily,
  item: LocatableItem,
  { active, country, levelIds }: LocationCriteria
): boolean {
  if (country !== null && locationCountry(family, item) !== country) {
    return false;
  }
  return levelIds.every(level => {
    const codes = active[level] ?? [];
    return codes.length === 0 || codes.includes(areaCodeOf(item, level) ?? '');
  });
}
